/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Box, Button, Chip, InputAdornment, MenuItem, TextField, Typography, makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import SearchIcon from '@material-ui/icons/Search';
import AddIcon from '@material-ui/icons/Add';
import { Content, Header, InfoCard, Link, Page } from '@backstage/core-components';
import { useAnalytics } from '@backstage/core-plugin-api';
import { HowItWorks, type HowItWorksStep } from '../common/HowItWorks';
import { CLIENT_LABELS, MCP_CLIENT_IDS, type McpClientId } from '../mcpConnect/types';
import { McpConnectDialog } from './McpConnectDialog';
import { McpEntryDialog } from './McpEntryDialog';
import { PendingProposals } from './PendingProposals';
import { McpServerCard } from './McpServerCard';
import { canMorph, morph } from './viewTransition';
import { NO_FILTERS, filterEntries, type CatalogFilters } from './filters';
import type { McpCatalogEntry } from './entries';
import { useMcpServers } from './useMcpServers';
import { useProposalsApi } from './proposals';

const ADD_GUIDE = 'https://github.com/m42-platform/backstage-config/tree/main/catalog/mcp';

type StepNotes = { loaded?: string; shown?: string; clients?: string };

const VISITED_KEY = 'm42.mcpCatalog.visited';

// Open on the first visit, collapsed after that; a reader's own toggle (stored
// under the explainer's collapseKey) always wins over this default. Read here,
// recorded in an effect, so a discarded render can't use up the first visit.
const firstVisit = (): boolean => {
  try {
    return localStorage.getItem(VISITED_KEY) === null;
  } catch {
    return true;
  }
};

/**
 * The explainer's steps, each carrying a live figure from the page's own data:
 * what loaded from backstage-config, how much the filters show, and how many
 * clients the list covers. A failed or empty load says so in words rather than
 * leaving the stage looking quiet.
 */
const howItWorksSteps = (notes: StepNotes): HowItWorksStep[] => [
  {
    title: 'Loading configuration',
    note: notes.loaded,
    body: (
      <>
        Entries come from <code>catalog/mcp/</code> in <Link to={ADD_GUIDE}>backstage-config</Link> and load here
        moments after a merge; its README explains how to add one.
      </>
    ),
  },
  {
    title: 'Find',
    note: notes.shown,
    body: 'Search by name, provider or tag, or filter to the clients you use. A card lists only the clients someone tested.',
  },
  {
    title: 'Connect',
    note: notes.clients,
    body: 'Open a server and pick your client: use 1-Click Connect where the client supports it, or copy the command or config. You sign in as yourself in that client.',
  },
];

const useStyles = makeStyles(theme => ({
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 308px',
    gap: theme.spacing(3),
    alignItems: 'start',
    [theme.breakpoints.down('md')]: { gridTemplateColumns: '1fr' },
  },
  mainCol: { minWidth: 0 },
  sideCol: {
    position: 'sticky',
    top: theme.spacing(2),
    [theme.breakpoints.down('md')]: { position: 'static' },
  },
  aboutIntro: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  aboutNote: { marginTop: theme.spacing(1) },
  // Same title scale as the How it works block beside it; doubled to outrank
  // InfoCard's own header-title rule.
  aboutTitle: { '&&': { fontSize: '0.9375rem', fontWeight: 600 } },
  controls: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: theme.spacing(1.5), marginBottom: theme.spacing(2) },
  add: { height: 40, whiteSpace: 'nowrap' },
  search: { minWidth: 240, flex: '1 1 280px', maxWidth: 420 },
  client: { minWidth: 180 },
  tags: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(0.75) },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: theme.spacing(2) },
  state: { padding: theme.spacing(6, 2), textAlign: 'center', color: theme.palette.text.secondary },
  stateActions: {
    display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center',
    gap: theme.spacing(1), marginTop: theme.spacing(1.5),
  },
  details: { marginTop: theme.spacing(1.5), fontSize: '0.75rem', '& summary': { cursor: 'pointer' } },
  srOnly: {
    position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
    overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
  },
}));

/**
 * MCP Catalog (PLAT-2278): vetted remote MCP servers from the software catalog
 * (API entities of spec.type mcp-server that the backend's McpCatalogProcessor
 * stamped m42.io/mcp-vetted=true), each installable per client through the same
 * McpInstallPanel the Home banner uses.
 */
export const McpCatalogPage = () => {
  const classes = useStyles();
  const analytics = useAnalytics();
  const { value: entries, loading, error, retry } = useMcpServers();
  const proposals = useProposalsApi();
  const [canPropose, setCanPropose] = useState(false);
  useEffect(() => {
    let live = true;
    proposals.capabilities().then(c => live && setCanPropose(Boolean(c?.canPropose))).catch(() => live && setCanPropose(false));
    return () => { live = false; };
  }, [proposals]);
  const [filters, setFilters] = useState<CatalogFilters>(NO_FILTERS);
  const [open, setOpen] = useState<McpCatalogEntry | null>(null);
  const [editor, setEditor] = useState<{ mode: 'add' } | { mode: 'edit'; name: string } | null>(null);
  const [pendingRefresh, setPendingRefresh] = useState(0);
  const existingNames = useMemo(() => (entries ?? []).map(e => e.descriptor.name), [entries]);
  const [morphing, setMorphing] = useState<string | null>(null);
  const [morphed, setMorphed] = useState(false);
  const transitionGeneration = useRef(0);
  const closingRef = useRef(false);
  const pendingEdit = useRef<string | null>(null);
  const editReturnTitle = useRef<string | null>(null);

  const openPendingEdit = () => {
    const name = pendingEdit.current;
    if (!name) return;
    pendingEdit.current = null;
    setEditor({ mode: 'edit', name });
  };

  const close = () => {
    if (closingRef.current) return;
    const generation = ++transitionGeneration.current;
    const card = open;
    const viaMorph = canMorph() && !!card;
    setMorphed(viaMorph);
    if (viaMorph && card) {
      closingRef.current = true;
      morph(() => {
        setOpen(null);
        setMorphing(card.entityRef);
        closingRef.current = false;
      }).then(() => {
        if (transitionGeneration.current === generation) setMorphing(null);
      });
    } else {
      setOpen(null);
    }
  };

  const tags = useMemo(() => [...new Set((entries ?? []).flatMap(e => e.tags))].sort(), [entries]);
  const visible = useMemo(() => filterEntries(entries ?? [], filters), [entries, filters]);
  const [openByDefault] = useState(firstVisit);
  useEffect(() => {
    try {
      localStorage.setItem(VISITED_KEY, '1');
    } catch {
      /* private mode: every visit counts as the first */
    }
  }, []);
  const filtered = Boolean(filters.query || filters.client || filters.tag);
  const { steps, summary, announcement } = useMemo(() => {
    const total = entries?.length ?? 0;
    const clients = new Set((entries ?? []).flatMap(e => e.descriptor.clients)).size;
    let loaded: string;
    if (loading) loaded = 'loading…';
    else if (error) loaded = 'couldn’t load';
    else loaded = total ? `${total} loaded` : 'none loaded';
    const clientsLabel = `${clients} client${clients === 1 ? '' : 's'}`;
    return {
      steps: howItWorksSteps({
        loaded,
        shown: total ? (visible.length === total ? `all ${total} shown` : `${visible.length} of ${total} shown`) : undefined,
        clients: total ? clientsLabel : undefined,
      }),
      // What the collapsed explainer keeps saying: the live figures and the promise.
      summary: total
        ? `${visible.length === total ? `${total} server${total === 1 ? '' : 's'}` : `${visible.length} of ${total} servers shown`} · ${clientsLabel} · each client tested end to end`
        : `MCP servers: ${loaded}`,
      // Announced politely only once the reader has filtered, not on page load.
      announcement: total && filtered ? `${visible.length} of ${total} MCP servers shown` : '',
    };
  }, [entries, visible, loading, error, filtered]);
  const set = (patch: Partial<CatalogFilters>, kind: string, val: string) => {
    setFilters(f => ({ ...f, ...patch }));
    analytics.captureEvent('filter', `mcp-catalog-filter:${kind}:${val}`);
  };

  // The no-match chips clear on click, Enter and Space as well as MUI's
  // Backspace/Delete, since they are announced as buttons.
  const clearQuery = () => setFilters(f => ({ ...f, query: '' }));
  const clearClient = () => setFilters(f => ({ ...f, client: null }));
  const clearTag = () => setFilters(f => ({ ...f, tag: null }));

  let body: React.ReactNode;
  if (loading) {
    body = (
      <Box className={classes.grid} aria-busy="true" aria-label="Loading MCP servers">
        {[0, 1, 2, 3, 4, 5].map(i => <Skeleton key={i} variant="rect" height={168} />)}
      </Box>
    );
  } else if (error) {
    body = (
      <Box className={classes.state} role="alert">
        <Typography>Couldn’t load the MCP Catalog. Retry; if it keeps failing, the catalog may be unavailable.</Typography>
        <Box className={classes.stateActions}>
          <Button variant="outlined" color="primary" onClick={retry}>Retry</Button>
        </Box>
        <details className={classes.details}>
          <summary>Details</summary>
          {error.message}
        </details>
      </Box>
    );
  } else if (!entries?.length) {
    body = (
      <Box className={classes.state}>
        <Typography>No MCP servers yet.</Typography>
        <Link to={ADD_GUIDE}>How to add one</Link>
      </Box>
    );
  } else if (!visible.length) {
    body = (
      <Box className={classes.state}>
        <Typography>No MCP server matches these filters.</Typography>
        <Box className={classes.stateActions} role="group" aria-label="Active filters">
          {filters.query && (
            <Chip
              label={`“${filters.query}”`}
              aria-label={`Remove search “${filters.query}”`}
              onClick={clearQuery}
              onDelete={clearQuery}
            />
          )}
          {filters.client && (
            <Chip
              label={`Works with ${CLIENT_LABELS[filters.client]}`}
              aria-label={`Remove filter Works with ${CLIENT_LABELS[filters.client]}`}
              onClick={clearClient}
              onDelete={clearClient}
            />
          )}
          {filters.tag && (
            <Chip label={filters.tag} aria-label={`Remove tag ${filters.tag}`} onClick={clearTag} onDelete={clearTag} />
          )}
          <Button variant="outlined" color="primary" onClick={() => setFilters(NO_FILTERS)}>Clear filters</Button>
        </Box>
        <Box className={classes.stateActions}>
          <Link to={ADD_GUIDE}>Not here? Propose one</Link>
        </Box>
      </Box>
    );
  } else {
    body = (
      <Box className={classes.grid}>
        {visible.map(entry => (
          <McpServerCard
            key={entry.entityRef}
            entry={entry}
            morphing={morphing === entry.entityRef}
            onConnect={() => {
              ++transitionGeneration.current;
              const viaMorph = canMorph();
              setMorphed(viaMorph);
              if (viaMorph) {
                flushSync(() => setMorphing(entry.entityRef));
                morph(() => {
                  setMorphing(null);
                  setOpen(entry);
                });
              } else {
                setOpen(entry);
              }
              analytics.captureEvent('click', `mcp-catalog-open:${entry.descriptor.name}`);
            }}
          />
        ))}
      </Box>
    );
  }

  return (
    <Page themeId="tool">
      <Header title="MCP Catalog" subtitle="Vetted MCP servers — pick one, pick your AI client, connect." />
      <Content>
        <div className={classes.layout}>
          <div className={classes.mainCol}>
            <HowItWorks
              variant="plain"
              collapseKey="m42.mcpCatalog.howItWorks"
              defaultOpen={openByDefault}
              summary={summary}
              lead={
                <>
                  MCP servers you can connect to your AI editor, vetted by the platform team: each client an entry lists was tried end to end — added, signed in, one real tool call.
                </>
              }
              steps={steps}
            />
            <Box className={classes.controls}>
              {canPropose && (
                <Button variant="outlined" color="primary" startIcon={<AddIcon />} className={classes.add} onClick={() => setEditor({ mode: 'add' })}>
                  Add MCP server
                </Button>
              )}
              <TextField
                className={classes.search}
                size="small"
                variant="outlined"
                placeholder="Search servers, providers, tags"
                inputProps={{ 'aria-label': 'Search MCP servers' }}
                value={filters.query}
                onChange={e => setFilters(f => ({ ...f, query: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
              <TextField
                select
                id="mcp-client"
                className={classes.client}
                size="small"
                variant="outlined"
                label="Works with"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                value={filters.client ?? ''}
                onChange={e => set({ client: (e.target.value || null) as McpClientId | null }, 'client', e.target.value || 'any')}
              >
                <MenuItem value="">Any client</MenuItem>
                {MCP_CLIENT_IDS.map(id => <MenuItem key={id} value={id}>{CLIENT_LABELS[id]}</MenuItem>)}
              </TextField>
              <Box className={classes.tags} role="group" aria-label="Filter by tag">
                {tags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    clickable
                    color={filters.tag === tag ? 'primary' : 'default'}
                    variant={filters.tag === tag ? 'default' : 'outlined'}
                    aria-pressed={filters.tag === tag}
                    onClick={() => set({ tag: filters.tag === tag ? null : tag }, 'tag', tag)}
                  />
                ))}
              </Box>
            </Box>
            <Typography component="h2" className={classes.srOnly}>Servers</Typography>
            <Typography role="status" aria-live="polite" className={classes.srOnly}>{announcement}</Typography>
            {body}
            {canPropose && <PendingProposals refresh={pendingRefresh} />}
            <McpConnectDialog
              entry={open} morphed={morphed} client={filters.client} onClose={close}
              onEdit={canPropose && open ? () => {
                pendingEdit.current = open.descriptor.name;
                editReturnTitle.current = open.descriptor.title;
                close();
              } : undefined}
              onExited={openPendingEdit}
            />
            <McpEntryDialog target={editor} existingNames={existingNames} onClose={() => {
              setEditor(null);
              const title = editReturnTitle.current;
              editReturnTitle.current = null;
              if (title) queueMicrotask(() => {
                [...document.querySelectorAll<HTMLButtonElement>('button[aria-label]')]
                  .find(button => button.getAttribute('aria-label') === `Connect ${title}`)?.focus();
              });
            }} onSwitchToEdit={name => setEditor({ mode: 'edit', name })} onSubmitted={() => setPendingRefresh(n => n + 1)} />
          </div>
          <aside className={classes.sideCol}>
            <InfoCard title="About this list" titleTypographyProps={{ component: 'h2', className: classes.aboutTitle }}>
              <Typography className={classes.aboutIntro}>
                Only servers merged into <code>backstage-config/catalog/mcp/</code> appear here — the same server declared anywhere else never does. Sign-ins are per user: each client keeps its own session, there are no shared tokens, and this page never handles them.
              </Typography>
              <Typography className={`${classes.aboutIntro} ${classes.aboutNote}`}>
                <strong>Experimental</strong> means at least one listed client needed a workaround to pass testing.
              </Typography>
            </InfoCard>
          </aside>
        </div>
      </Content>
    </Page>
  );
};
